import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { QrLabelPrint } from '../shared/QrLabelPrint';

/**
 * Panneau étiquette QR / code-barres imprimable (US-EQ-03 / US-EQ-11).
 *
 * Wrapper qui charge l'étiquette équipement depuis le backend
 * (`GET /equipements/:id/qr-label` → { code, designation, url, numero_inventaire })
 * et délègue le rendu au composant partagé `<QrLabelPrint>`.
 */

interface QrLabel {
  id: string;
  code: string;
  designation: string;
  numero_inventaire: string | null;
  url: string;
  label_html: string;
}

export function QrLabelPanel({ equipementId }: { equipementId: string }) {
  const [data, setData] = useState<QrLabel | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: QrLabel }>(`/equipements/${equipementId}/qr-label`);
        setData(res.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [equipementId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  if (errorMessage || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-red-700">{errorMessage ?? 'Étiquette indisponible'}</p>
      </div>
    );
  }

  return (
    <QrLabelPrint
      code={data.code}
      label={data.designation}
      url={data.url}
      sublabel={data.numero_inventaire ? `Inv : ${data.numero_inventaire}` : undefined}
    />
  );
}
